import { BrandLogo } from '@shared/branding/BrandLogo';
import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { cn } from '@shared/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@shared/ui/avatar';
import { Button } from '@shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@shared/ui/dropdown-menu';
import { ThemeToggle } from '@shared/ui/theme-toggle';
import { LanguageSwitcher } from '@shared/i18n/LanguageSwitcher';
import { User, Settings, LogOut, Activity, ChevronDown, Bell } from 'lucide-react';
import type { UserProfile } from '@shared/types/auth';
import { useAuth } from '@shared/hooks/useAuth';
import { localizePath, stripLocaleFromPathname, useLocale } from '@shared/i18n';

interface PatientMobileHeaderProps {
  user: UserProfile;
  className?: string;
}

/**
 * Compact mobile header for patient portal
 *
 * Features:
 * - Sticky positioning with backdrop blur
 * - Jema logo
 * - User avatar dropdown with profile actions
 * - Theme toggle and language switcher
 * - Accessible with proper ARIA labels
 */
export const PatientMobileHeader: React.FC<PatientMobileHeaderProps> = ({ user, className }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { locale } = useLocale();
  const { i18n } = useLingui();
  const { signOut } = useAuth();
  const unreadCount = 0;

  // Memoize user display info to prevent recalculations
  const userInfo = React.useMemo(() => {
    const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
    const displayName = fullName || user.email?.split('@')[0] || 'Patient';
    const initials =
      displayName
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2) || 'PA';
    const fallbackAvatar = `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(displayName)}`;

    return {
      displayName,
      initials,
      email: user.email || '',
      avatarSrc: fallbackAvatar,
    };
  }, [user.first_name, user.last_name, user.email]);

  // Sign out handler with error handling
  const handleSignOut = React.useCallback(async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out failed:', error);
      // Could show a toast notification here
    }
  }, [signOut]);

  // Navigation helper
  const handleNavigate = React.useCallback(
    (path: string) => {
      navigate(localizePath(path, locale));
    },
    [locale, navigate]
  );

  // Get current page title for header (optional enhancement)
  const pageTitle = React.useMemo(() => {
    switch (stripLocaleFromPathname(location.pathname)) {
      case '/dashboard':
        return i18n._({
          id: 'shared.lib.navigation.patient.dashboard',
          message: 'Dashboard',
        });
      case '/quotes':
        return i18n._({
          id: 'shared.lib.navigation.patient.quotes',
          message: 'Quotes',
        });
      case '/travel':
        return i18n._({
          id: 'shared.lib.navigation.patient.travel',
          message: 'Travel',
        });
      case '/payments':
        return i18n._({
          id: 'shared.lib.navigation.patient.payments',
          message: 'Payments',
        });
      case '/treatment':
        return i18n._({
          id: 'shared.lib.navigation.patient.treatment',
          message: 'Treatment',
        });
      case '/profile':
        return i18n._({
          id: 'shared.layout.PatientMobileHeader.profile',
          message: 'Profile',
        });
      default:
        return null;
    }
  }, [i18n, locale, location.pathname]);

  return (
    <header
      className={cn(
        // Positioning
        'sticky top-0 z-40',
        // Layout
        'flex items-center justify-between gap-2',
        'h-[calc(3.5rem+env(safe-area-inset-top,0px))] px-3 sm:px-4',
        // Styling
        'bg-background/95 backdrop-blur-xl supports-[backdrop-filter]:bg-background/80',
        'border-b border-border/40 shadow-sm',
        // Safe area for iOS notch
        'pt-safe',
        className
      )}
      role='banner'
    >
      {/* Logo / Home link */}
      <Link
        to={localizePath('/dashboard', locale)}
        className={cn(
          'flex items-center shrink-0',
          'focus-visible:outline-none focus-visible:ring-2',
          'focus-visible:ring-brand-blue focus-visible:ring-offset-2',
          'rounded-lg p-1'
        )}
        aria-label={t({
          id: 'shared.layout.PatientMobileHeader.go-to-dashboard',

          message: 'Go to dashboard',
        })}
      >
        <BrandLogo compact className='h-8 w-8' />
      </Link>
      {/* Page title - shown on larger mobile screens */}
      {pageTitle && (
        <span
          className={cn(
            'hidden xs:block',
            'text-sm font-medium text-muted-foreground',
            'truncate flex-1 text-center'
          )}
        >
          {pageTitle}
        </span>
      )}
      {/* Right Actions */}
      <div className='flex items-center gap-0.5 shrink-0'>
        {/* Theme Toggle */}
        <ThemeToggle />

        {/* Language Switcher */}
        <LanguageSwitcher />

        {/* User Menu Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant='ghost'
              size='sm'
              className={cn(
                'gap-1 pl-1 pr-1.5',
                'h-9 min-w-9', // Touch target
                'focus-visible:ring-brand-blue'
              )}
              aria-label={t({
                id: 'shared.layout.PatientMobileHeader.user-menu',

                message: 'User menu',
              })}
            >
              <Avatar className='h-7 w-7 border border-border/50'>
                <AvatarImage src={userInfo.avatarSrc} alt={userInfo.displayName} />
                <AvatarFallback
                  className={cn('text-xs font-medium', 'bg-brand-blue/10 text-brand-blue')}
                >
                  {userInfo.initials}
                </AvatarFallback>
              </Avatar>
              <ChevronDown
                className='h-3 w-3 text-muted-foreground hidden sm:block'
                aria-hidden='true'
              />
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align='end' className='w-56' sideOffset={8}>
            {/* User Info */}
            <DropdownMenuLabel className='font-normal'>
              <div className='flex flex-col gap-1'>
                <p className='text-sm font-medium leading-none truncate'>{userInfo.displayName}</p>
                <p className='text-xs text-muted-foreground leading-none truncate'>
                  {userInfo.email}
                </p>
              </div>
            </DropdownMenuLabel>

            <DropdownMenuSeparator />

            {/* Menu Items */}
            <DropdownMenuItem
              onClick={() => handleNavigate('/treatment')}
              className='cursor-pointer'
            >
              <Activity className='mr-2 h-4 w-4' aria-hidden='true' />
              <Trans id='shared.layout.PatientMobileHeader.treatment'>Treatment Records</Trans>
            </DropdownMenuItem>

            <DropdownMenuItem onClick={() => handleNavigate('/profile')} className='cursor-pointer'>
              <User className='mr-2 h-4 w-4' aria-hidden='true' />
              <Trans id='shared.layout.PatientMobileHeader.profile'>Profile</Trans>
            </DropdownMenuItem>

            <DropdownMenuItem disabled className='cursor-not-allowed opacity-60'>
              <Bell className='mr-2 h-4 w-4' aria-hidden='true' />
              <Trans id='shared.layout.PatientMobileHeader.notifications'>Notifications</Trans>
              {unreadCount > 0 ? (
                <span className='ml-auto text-xs text-muted-foreground'>
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              ) : null}
            </DropdownMenuItem>

            <DropdownMenuItem className='cursor-pointer'>
              <Settings className='mr-2 h-4 w-4' aria-hidden='true' />
              <Trans id='shared.layout.PatientMobileHeader.settings'>Settings</Trans>
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            <DropdownMenuItem
              onClick={handleSignOut}
              className={cn(
                'cursor-pointer',
                'text-destructive focus:text-destructive',
                'focus:bg-destructive/10'
              )}
            >
              <LogOut className='mr-2 h-4 w-4' aria-hidden='true' />
              <Trans id='shared.layout.PatientMobileHeader.sign-out'>Sign out</Trans>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
